/**
 * Stripe Payment Service
 * GTRA 活动支付服务
 */

import { loadStripe } from '@stripe/stripe-js';
import api from './api';

// Stripe 实例
let stripePromise: Promise<any> | null = null;

// 获取 Stripe 实例
export const getStripe = () => {
  if (!stripePromise) {
    stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLIC_KEY || '');
  }
  return stripePromise;
};

// 价格类型
export type PriceType = 'early_bird' | 'regular' | 'token_free' | 'free';

// 价格时段
export interface PriceTier {
  type: PriceType;
  label: string;
  labelEn: string;
  price: number;
  start_date: string;
  end_date: string;
  is_active: boolean;
  description: string;
  description_en: string;
}

// 活动定价信息
export interface EventPricing {
  event_id: number;
  is_paid_event: boolean;
  currency: string;
  early_bird_enabled: boolean;
  early_bird_price: number;
  early_bird_start_date: string;
  early_bird_end_date: string;
  regular_price: number;
  price_end_date: string;
  token_gating_enabled: boolean;
  token_symbol: string;
  token_contract_address: string;
  token_min_holding: number;
  token_holders_only: boolean;
  stripe_product_id: string;
  stripe_price_id: string;
  available_tiers: PriceTier[];
  current_tier: string;
  current_price: number;
}

// 支付记录
export interface Payment {
  id: number;
  registration_id: number;
  event_id: number;
  user_id: string;
  user_email: string;
  user_name?: string;
  event_title?: string;
  amount: number;
  currency: string;
  price_type: PriceType;
  stripe_payment_intent_id: string;
  stripe_session_id: string;
  stripe_status: 'pending' | 'completed' | 'failed' | 'refunded';
  is_token_holder: boolean;
  token_symbol: string;
  token_holding: number;
  token_verified: boolean;
  payment_method: 'stripe' | 'token' | 'free';
  transaction_hash: string;
  refund_amount: number;
  refund_reason: string;
  refunded_at: string;
  completed_at: string;
  created_at: string;
}

// 创建支付会话
export interface CreatePaymentRequest {
  event_id: number;
  user_email: string;
  user_id?: string;
  registration_id?: number;
  wallet_address?: string;
  payment_method: 'stripe' | 'token' | 'free';
}

// 创建支付会话响应
export interface CreatePaymentResponse {
  session_id?: string;
  payment_id: number;
  checkout_url?: string;
  status: 'completed' | 'pending';
  payment_method: string;
  amount: number;
  message?: string;
}

// 获取 Stripe 公钥
export const getStripePublicKey = async (): Promise<string> => {
  const response = await api.get('/public/stripe/public-key');
  return response.data.public_key;
};

// 获取活动定价信息
export const getEventPricing = async (eventId: number): Promise<EventPricing> => {
  const response = await api.get(`/public/events/${eventId}/pricing`);
  return response.data;
};

// 创建支付会话
export const createCheckoutSession = async (data: CreatePaymentRequest): Promise<CreatePaymentResponse> => {
  const response = await api.post('/public/payments/create-session', data);
  return response.data;
};

// 获取支付状态
export const getPaymentStatus = async (paymentId: number): Promise<Payment> => {
  const response = await api.get(`/public/payments/status/${paymentId}`);
  return response.data;
};

// 通过 Session ID 获取支付状态
export const getPaymentBySession = async (sessionId: string): Promise<Payment> => {
  const response = await api.get(`/public/payments/session/${sessionId}`);
  // axios response: { data: { code, message, data: payment } }
  const result = response.data;
  // 如果返回的是包装结构，取其中的 data
  return result.data || result;
};

// Token 访问请求
export interface TokenAccessRequest {
  event_id: number;
  wallet_address: string;
  email: string;
}

export interface TokenAccessResponse {
  status: 'pending' | 'verified';
  message: string;
  verify_code?: string;
}

export const requestTokenAccess = async (data: TokenAccessRequest): Promise<TokenAccessResponse> => {
  const response = await api.post('/public/token-access/request', data);
  return response.data;
};

// 验证 Token 持有者
export interface VerifyTokenRequest {
  event_id: number;
  wallet_address: string;
  code: string;
}

export const verifyTokenHolder = async (data: VerifyTokenRequest): Promise<TokenAccessResponse> => {
  const response = await api.post('/public/token-access/verify', data);
  return response.data;
};

// Stripe Checkout 重定向
export const redirectToStripeCheckout = async (data: CreatePaymentRequest) => {
  const result = await createCheckoutSession(data);
  
  if (result.checkout_url) {
    window.location.href = result.checkout_url;
    return result;
  }
  
  return result;
};

// 格式化价格显示
export const formatPrice = (amount: number, currency: string = 'USD'): string => {
  if (amount === 0) return 'Free';
  
  const symbols: Record<string, string> = {
    USD: '$',
    EUR: '€',
    GBP: '£',
    CNY: '¥',
  };
  
  const symbol = symbols[currency] || currency + ' ';
  return `${symbol}${amount.toFixed(2)}`;
};

// 获取当前有效价格时段
export const getActivePriceTier = (pricing: EventPricing): PriceTier | null => {
  const now = new Date();
  
  // 优先检查早鸟价格
  if (pricing.early_bird_enabled) {
    const earlyBirdStart = new Date(pricing.early_bird_start_date);
    const earlyBirdEnd = new Date(pricing.early_bird_end_date);
    
    if (now >= earlyBirdStart && now <= earlyBirdEnd) {
      return pricing.available_tiers.find(t => t.type === 'early_bird') || null;
    }
  }
  
  // 检查正常价格
  if (pricing.regular_price > 0) {
    const priceEnd = pricing.price_end_date ? new Date(pricing.price_end_date) : null;
    
    if (!priceEnd || now <= priceEnd) {
      return pricing.available_tiers.find(t => t.type === 'regular') || null;
    }
  }
  
  // 默认返回第一个有效时段
  return pricing.available_tiers.find(t => t.is_active) || null;
};

// 计算价格节省
export const calculateSavings = (pricing: EventPricing): { amount: number; percent: number } | null => {
  if (!pricing.early_bird_enabled || pricing.regular_price === 0) {
    return null;
  }
  
  const savings = pricing.regular_price - pricing.early_bird_price;
  const percent = (savings / pricing.regular_price) * 100;
  
  return {
    amount: savings,
    percent: Math.round(percent),
  };
};
